import { Alert, AlertDescription } from "~/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import useToast from "~/components/ui/toast";
import { SettingsGroup } from "~/pages/manifold-nextgen/components/settings/SettingsGroup";
import { SettingsPage } from "~/pages/manifold-nextgen/components/settings/SettingsPage";
import { SettingsRow } from "~/pages/manifold-nextgen/components/settings/SettingsRow";
import type { NotificationDelivery } from "~/services/notifications";
import { useNotificationDelivery } from "./useNotificationDelivery";

const DELIVERY_CHOICES: { value: NotificationDelivery; label: string }[] = [
  { value: "in-app", label: "In-app only" },
  { value: "email", label: "Email only" },
  { value: "both", label: "In-app and email" },
  { value: "none", label: "Off" },
];

/**
 * The profile's Notifications tab: where the user chooses how notifications reach
 * them. A choice saves as soon as it is made, since it is one dropdown.
 */
export const ProfileNotificationsTab = () => {
  const { showSuccess, showError } = useToast();
  const { delivery, error, isSaving, save } = useNotificationDelivery();

  const handleChange = async (next: NotificationDelivery) => {
    try {
      await save(next);
      showSuccess("Notification settings saved");
    } catch {
      showError("Unable to save your notification settings");
    }
  };

  const renderControl = () => {
    if (delivery === null) {
      return <Skeleton data-testid="notification-delivery-loading" className="h-9 w-56" />;
    }
    return (
      <Select
        value={delivery}
        onValueChange={(next) => handleChange(next as NotificationDelivery)}
        disabled={isSaving}
      >
        <SelectTrigger className="w-56" aria-label="Delivery">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {DELIVERY_CHOICES.map((choice) => (
            <SelectItem key={choice.value} value={choice.value}>
              {choice.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  };

  if (error) {
    return (
      <SettingsPage title="Notifications" description="Choose how Manifold notifies you.">
        <Alert variant="destructive">
          <AlertDescription>Unable to load your notification settings.</AlertDescription>
        </Alert>
      </SettingsPage>
    );
  }

  return (
    <SettingsPage title="Notifications" description="Choose how Manifold notifies you.">
      <SettingsGroup>
        <SettingsRow
          title="Notification delivery"
          description="Choose where you hear about access changes, such as a project shared with you."
        >
          {renderControl()}
        </SettingsRow>
      </SettingsGroup>
    </SettingsPage>
  );
};
